import { create } from 'zustand';
import * as api from '../api';
import type { Headline, ReadHeadline } from '../types';

interface FetchOpts {
  sourceSlug?: string;
  classifierId?: number;
}

interface HeadlineState {
  unread: Headline[];
  read: ReadHeadline[];
  interestMode: 'text' | 'classifiers' | null;
  summaries: Record<number, string[] | 'loading'>;
  summarizedIds: number[];
  fetchHeadlines: (opts?: FetchOpts) => Promise<void>;
  clickHeadline: (id: number) => Promise<string>;
  markAllRead: (opts?: FetchOpts) => Promise<void>;
  toggleSummary: (id: number) => Promise<void>;
}

export const useHeadlineStore = create<HeadlineState>((set, get) => ({
  unread: [],
  read: [],
  interestMode: null,
  summaries: {},
  summarizedIds: [],
  fetchHeadlines: async (opts) => {
    const { unread, read, interestMode } = await api.fetchHeadlines(opts);
    set({ unread, read, interestMode, summarizedIds: [] });
  },
  clickHeadline: async (id) => {
    const { url } = await api.clickHeadline(id);
    const state = get();
    const clicked = state.unread.find((h) => h.id === id);
    if (clicked) {
      set({
        unread: state.unread.filter((h) => h.id !== id),
        read: [
          { ...clicked, read_at: new Date().toISOString() },
          ...state.read,
        ],
        summarizedIds: state.summarizedIds.filter((sid) => sid !== id),
      });
    }
    return url;
  },
  markAllRead: async (opts) => {
    const state = get();
    const ids = state.unread.map((h) => h.id);
    if (ids.length === 0) return;
    await api.markAllRead(ids);
    const { unread, read, interestMode } = await api.fetchHeadlines(opts);
    set({ unread, read, interestMode, summarizedIds: [] });
  },
  toggleSummary: async (id) => {
    const { summaries, summarizedIds } = get();
    if (summaries[id]) {
      const next = { ...summaries };
      delete next[id];
      set({
        summaries: next,
        summarizedIds: summarizedIds.filter((sid) => sid !== id),
      });
      return;
    }
    set({
      summaries: { ...summaries, [id]: 'loading' },
      summarizedIds: summarizedIds.includes(id)
        ? summarizedIds
        : [...summarizedIds, id],
    });
    try {
      const { sentences } = await api.fetchSummary(id);
      set({ summaries: { ...get().summaries, [id]: sentences } });
    } catch {
      const s = get();
      const next = { ...s.summaries };
      delete next[id];
      set({
        summaries: next,
        summarizedIds: s.summarizedIds.filter((sid) => sid !== id),
      });
    }
  },
}));
